/**
 * The lead profile's cards: which column each is in, what order, and the fold.
 *
 * ── What this list used to be, and why it changed ──────────────────────────
 *
 * It was eleven sections stacked down one narrow drawer, ordered by Kyu's four
 * jobs after an assessment found Contact sitting sixth of nine, ~340 lines
 * down. The order fixed the symptom. The relayout removes the cause: the
 * record has three columns now, so "how far down is it" stops being the only
 * hierarchy available.
 *
 * Kyu's four jobs, in his words, still decide what goes where:
 *   quickly see who the lead is · get the contact details fast ·
 *   understand where they stand right now · see what action is needed next
 *
 * The first two are answered ABOVE this list entirely. Name, email and phone
 * are in the identity block at the top of the record column, always visible,
 * with a copy button each. No card has to be opened and nothing has to be
 * scrolled to, which is what those two jobs were actually asking for.
 */

/** Which column of the profile shell a card lives in. */
export type LeadPanelColumn = 'record' | 'associations';

export interface LeadPanelSectionSpec {
  key: string;
  title: string;
  /**
   * Record column, or associations column.
   *
   * The rule: if changing it changes what the lead IS, it is a property and
   * goes in the record column. If it is another record with its own page, it
   * is an association and goes on the right. A deal is not a field on a lead.
   */
  column: LeadPanelColumn;
  /** Can this one be folded away? */
  foldable: boolean;
  /** Which of the four jobs it serves, or null for governance. */
  job: string | null;
}

/**
 * The six cards, in the order they render, column by column.
 *
 * Eleven sections became six cards. Nothing was dropped: three pairs merged
 * because they were always one question asked twice.
 *
 *   Status            was Status + Next action + Follow-up round
 *   Source and origin was Lead Source + Lead Origin + Contactable
 *   Categories        was Categories + Nurture
 *
 * ── Everything folds now, and nothing is folded ───────────────────────────
 *
 * Five cards used to be unfoldable, on the reasoning that a section answering
 * one of the four jobs must not be hideable. That reasoning belonged to a
 * single column, where folding something meant it was the only way to reach
 * what was under it. In three columns a card is a card: it folds, and it
 * starts open, so nobody arrives at a profile that looks broken.
 *
 * The two jobs that most needed protecting are no longer in this list at all.
 * They are in the identity block, which does not fold.
 */
export const LEAD_PANEL_SECTIONS: LeadPanelSectionSpec[] = [
  {
    key: 'status',
    title: 'Status',
    column: 'record',
    foldable: true,
    job: 'where they stand · what do I do next',
  },
  // First, and it holds three things that were three separate strips. A task,
  // a cadence and a stage are different questions, which is why they kept
  // their own labels inside the card rather than being flattened into one.
  {
    key: 'about',
    title: 'About this lead',
    column: 'record',
    foldable: true,
    job: 'who is this · contact fast',
  },
  {
    key: 'source-origin',
    title: 'Source and origin',
    column: 'record',
    foldable: true,
    job: null,
  },
  // Categories and Nurture were MISSING from the original assessment, which
  // counted seven sections where there were nine: neither draws a section
  // title of its own, so the scan walked past both. Kyu had named them
  // himself. Counting what is easy to count is how a section gets left
  // behind, and that is why they are written down here.
  { key: 'categories', title: 'Categories', column: 'record', foldable: true, job: null },
  // The company opens the associations column, ahead of the deals.
  //
  // Kyu, 15 September: "when you open a lead, it should show the company and
  // the deal... All of those three should always be seen right away." It was
  // only ever a FIELD with a small Open link beside it, and a field is a
  // different promise from an association: one tells you the value, the other
  // says there is a record here and takes you to it.
  //
  // Foldable like everything else. The first draft of this entry made it
  // unfoldable to protect it, which is the reasoning this file already
  // rejected two paragraphs up: in three columns, folding a card no longer
  // hides the one beneath it. Position is the protection, not a locked fold.
  {
    key: 'linkedin-start',
    title: 'Started on LinkedIn',
    column: 'associations',
    foldable: true,
    job: 'where they started',
  },
  {
    key: 'company',
    title: 'Company',
    column: 'associations',
    foldable: true,
    job: 'who is this',
  },
  {
    key: 'deals',
    title: 'Deals',
    column: 'associations',
    foldable: true,
    job: 'where they stand',
  },
  { key: 'lists', title: 'Lists', column: 'associations', foldable: true, job: null },
];

/**
 * Its own key, NOT the dashboard's.
 *
 * 8c stores a flat list of section keys under `tap.dashboard.collapsed`.
 * Sharing it would let a drawer key and a dashboard key collide — and they
 * nearly would: both modules have a section that could reasonably be called
 * 'lists' or 'status'. Two namespaces, no collision, no cleverness.
 */
const STORAGE_KEY = 'tap.leadpanel.collapsed';

export function toggleLeadSection(collapsed: readonly string[], key: string): string[] {
  return collapsed.includes(key) ? collapsed.filter((entry) => entry !== key) : [...collapsed, key];
}

/**
 * Per SECTION, never per lead.
 *
 * Worth stating because the obvious implementation keys the fold on the lead
 * id by accident, and then somebody who folded Lead Origin has to fold it
 * again on every lead they open — which is more annoying than the clutter it
 * was meant to fix.
 *
 * Guarded reads and writes, same as the dashboard: a browser set to block site
 * data throws on ACCESS rather than returning nothing, and a drawer that will
 * not render because it could not remember a fold is a worse bug than the one
 * being fixed.
 */
export function readCollapsedLeadSections(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    // Only keys this list still knows about survive a read. Every card folds
    // today, so this no longer filters by the flag in practice — what it
    // catches is a key for a card that has been renamed or merged away, which
    // the relayout just did to five of them. Left in storage, 'lead-origin'
    // would sit there forever waiting to hide something that came back under
    // the same name.
    const foldable = new Set(
      LEAD_PANEL_SECTIONS.filter((section) => section.foldable).map((section) => section.key),
    );
    return parsed.filter(
      (entry): entry is string => typeof entry === 'string' && foldable.has(entry),
    );
  } catch {
    return [];
  }
}

export function writeCollapsedLeadSections(collapsed: readonly string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
  } catch {
    // The drawer works either way; only the memory of the fold is lost.
  }
}
